import { TemplateOutputsRunRateTable } from 'Features/Projects/Scenarios/Templates/TemplateResults/TemplateResults.types'
import { Box, IconButton, Slider, SxProps, Theme } from '@mui/material'
import { GridCellParams, GridColDef, GridRowId } from '@mui/x-data-grid-premium'
import { Dispatch, SetStateAction, useMemo } from 'react'
import { BaseTable } from 'Features/Projects/Table/BaseTable.view'
import clsx from 'clsx'
import { round } from 'lodash'
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'

type RunRateTableProps = {
  tableRows: TemplateOutputsRunRateTable[]
  setTableRows: Dispatch<SetStateAction<TemplateOutputsRunRateTable[]>>
  isLoading: boolean
}

export const RunRateTable = ({
  tableRows,
  setTableRows,
  isLoading,
}: RunRateTableProps) => {
  const calcProductionWeeks = (
    start_week: number,
    cycle: number
  ): boolean[] => {
    return [...Array(8)].map((_, i) => {
      const week = i + 1
      return week >= start_week && (week - start_week) % cycle === 0
    })
  }

  const updatedRow = (
    row: TemplateOutputsRunRateTable
  ): TemplateOutputsRunRateTable => {
    const { startWeek, cycleTime } = row
    const production_map = calcProductionWeeks(startWeek, cycleTime)

    const total_run_time: number = Array.from({ length: 8 }).reduce(
      (total: number, _, i) => {
        const weekKey = `week_${i + 1}` as keyof TemplateOutputsRunRateTable
        return total + (row[weekKey] as number)
      },
      0
    )

    const updatedRow: TemplateOutputsRunRateTable = { ...row }

    for (let i = 0; i < 8; i++) {
      const weekKey = `week_${i + 1}` as keyof TemplateOutputsRunRateTable
      // @ts-ignore
      updatedRow[weekKey] = production_map[i]
        ? round((total_run_time * cycleTime) / 8, 2)
        : 0
    }

    return updatedRow
  }

  type Mode = boolean // false for decrease, true for increase

  const handleStartWeekChange = (id: GridRowId, mode: Mode): void => {
    const row = tableRows.find((row) => row.id === id)

    if (!row) return

    const { startWeek } = row

    let newRow = { ...row, startWeek: mode ? startWeek + 1 : startWeek - 1 }

    newRow = updatedRow(newRow)

    const newTableRows = tableRows.map((tableRow) =>
      tableRow.id === newRow.id ? { ...tableRow, ...newRow } : tableRow
    )

    setTableRows(newTableRows)
  }

  const handleCycleChange = (
    id: GridRowId,
    newCycleValue: number | number[]
  ) => {
    const newCycle = Array.isArray(newCycleValue)
      ? newCycleValue[0]
      : newCycleValue

    const row = tableRows.find((row) => row.id === id)
    if (!row) return

    if (newCycle === row.cycleTime) return

    const weekKeys = [
      'week_1',
      'week_2',
      'week_3',
      'week_4',
      'week_5',
      'week_6',
      'week_7',
      'week_8',
    ] as const

    const totalSum = weekKeys.reduce((sum, key) => sum + row[key], 0)

    const newWeeks = Object.fromEntries(
      weekKeys.map((key) => [key, 0])
    ) as Record<keyof TemplateOutputsRunRateTable, number>

    for (let i = 0; i < 8; i += newCycle) {
      const weekIndex = Math.floor(i)
      const key = weekKeys[weekIndex]
      newWeeks[key] = round((totalSum * newCycle) / 8, 2)
    }

    //@ts-ignore
    const updatedRow: TemplateOutputsRunRateTable = {
      ...row,
      ...newWeeks,
      cycleTime: newCycle,
      startWeek: 1,
    }

    const newRows = tableRows.map((r) => (r.id === row.id ? updatedRow : r))

    setTableRows(newRows)
  }

  const COLUMN_WIDTH = 150

  const renderZero = (value: number) => {
    if (value === 0) {
      return <div></div>
    } else {
      return <div>{value}</div>
    }
  }

  const renderCycleCell = (
    params: GridCellParams<TemplateOutputsRunRateTable>
  ) => {
    const { id } = params.row
    const row = tableRows.find((r) => r.id === id)
    if (!row) return null

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        <Slider
          value={Math.log2(row.cycleTime)}
          min={0}
          max={3}
          track={false}
          step={1}
          marks={[
            { value: 0, label: '' },
            { value: 1, label: '' },
            { value: 2, label: '' },
            { value: 3, label: '' },
          ]}
          valueLabelDisplay="off"
          onChangeCommitted={(_, value) => {
            const newCycle = 2 ** (value as number)
            handleCycleChange(id, newCycle)
          }}
          sx={{
            '& .MuiSlider-mark': {
              height: 6,
              width: 2,
              borderRadius: 0,
              backgroundColor: 'secondary',
            },
            '& .MuiSlider-thumb': {
              height: 15,
              width: 5,
              borderRadius: 0,
              backgroundColor: 'primary',
            },
          }}
        />
      </Box>
    )
  }

  const renderMoveButtons = (
    params: GridCellParams<TemplateOutputsRunRateTable>
  ) => {
    const { id } = params.row

    const row = tableRows.find((row) => row.id === id)
    if (!row) return null
    const { startWeek, cycleTime } = row

    return (
      <Box
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
          }}
        >
          <IconButton
            disabled={startWeek >= cycleTime || startWeek === 8}
            onClick={() => handleStartWeekChange(id, true)}
          >
            <KeyboardArrowUp />
          </IconButton>
          <IconButton
            disabled={startWeek === 1}
            onClick={() => handleStartWeekChange(id, false)}
          >
            <KeyboardArrowDown />
          </IconButton>
        </Box>
        {startWeek}
      </Box>
    )
  }

  //@ts-ignore
  const columns: GridColDef[] = useMemo(() => {
    const base_columns = [
      {
        field: 'workcentreCode',
        headerName: 'Work Centre Code',
        width: COLUMN_WIDTH,
        type: 'string',
      },
      {
        field: 'skuGroupName',
        headerName: 'SKU Group Name',
        minWidth: COLUMN_WIDTH,
        type: 'string',
        flex: 1,
      },
      {
        field: 'batchGroupingCode',
        headerName: 'Batch Group',
        width: COLUMN_WIDTH,
        type: 'string',
      },
      {
        field: 'replenishmentTypeCode',
        headerName: 'Replenishment Type',
        width: COLUMN_WIDTH,
        type: 'string',
        cellClassName: (params: GridCellParams) => {
          return clsx('table', {
            foq: params.row.replenishmentTypeCode === 'FOQ',
          })
        },
      },
      {
        field: 'minimumProductionOrderQuantity',
        headerName: 'MOQ',
        width: 125,
        type: 'number',
      },
      {
        field: 'minimumProductionOrderIncrement',
        headerName: 'MOI',
        width: 125,
        type: 'number',
      },
      {
        field: 'cycleTime',
        headerName: 'Cycle Time',
        width: 170,
        renderCell: (params: GridCellParams) => {
          if (params.rowNode.type === 'pinnedRow') {
            return <div></div>
          } else {
            return renderCycleCell(params)
          }
        },
        type: 'number',
      },
      {
        field: 'startWeek',
        headerName: 'Start Week',
        renderCell: (params: GridCellParams) => {
          if (params.rowNode.type === 'pinnedRow') {
            return <div></div>
          } else {
            return renderMoveButtons(params)
          }
        },
        width: 140,
        type: 'number',
      },
    ]

    const week_columns = [...Array(8)].map((_, index) => {
      const weekNumber = index + 1
      return {
        field: `week_${weekNumber}`,
        headerName: `Week ${weekNumber}`,
        cellClassName: (params: GridCellParams) => {
          if (params.rowNode.type === 'pinnedRow') {
            return clsx('table', {
              aggregatedCell: true,
            })
          }
          return clsx('table', {
            zero: params.value !== 0,
          })
        },
        renderCell: (params: GridCellParams) => {
          if (params.rowNode.type === 'pinnedRow') {
            const value = params.value as number
            return (
              <strong style={{ color: 'var(--brand-blue-600)' }}>
                {value.toFixed(2)}
              </strong>
            )
          }
          return renderZero(params.value as number)
        },
        type: 'number',
        width: 100,
      }
    })

    return [...base_columns, ...week_columns]
  }, [tableRows])

  // Styles for runRate table
  const sx: SxProps<Theme> = {
    // Square top corners: the Save bar sits on top of this grid.
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    '& .table.zero': {
      backgroundColor: 'var(--state-complete-tint)',
      backgroundImage: `linear-gradient(45deg, rgba(255,255,255,0.2) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.2) 75%, transparent 75%, transparent)`,
      backgroundSize: '10px 10px',
    },
    '& .table.foq': {
      backgroundColor: 'var(--state-failed-tint)',
      backgroundImage: `linear-gradient(45deg, rgba(255,255,255,0.2) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.2) 75%, transparent 75%, transparent)`,
      backgroundSize: '10px 10px',
    },
    '& .table.aggregatedCell': {
      backgroundColor: 'var(--surface-0)',
      fontWeight: 'bold',
    },
    // No zebra here: the flagged cells carry the colour.
    '& .MuiDataGrid-row:nth-of-type(odd), & .MuiDataGrid-row.row--odd': {
      backgroundColor: 'var(--surface-0)',
    },
  }

  // Send data to API to correctly update

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        overflow: 'auto',
        flexDirection: 'column',
      }}
    >
      <BaseTable<TemplateOutputsRunRateTable>
        data={tableRows}
        columns={columns}
        isLoading={isLoading}
        updateTableData={() => {}}
        isUploading={false}
        isSaving={false}
        sx={sx}
        showTableToolBar={false}
      />
    </Box>
  )
}
